import classnames from 'classnames';
import { memo, useContext, type FC } from 'react';

import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { Coupon } from '@/domains/loyalty/components/Coupon/Coupon';
import { StringAbTests } from '@/domains/productDiscovery/ProductPage/constants';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { FlashMessages } from '@/domains/shoppingCart/FlashMessages/FlashMessages';

import CouponContext from '../../context/Coupon/Coupon.context';
import { useProductBundle } from '../../context/ProductBundle/ProductBundle.context';
import OutlineBundleA2C from './components/OutlineBundleA2C/OutlineBundleA2C';
import { OutlineBundleBody } from './components/OutlineBundleBody/OutlineBundleBody';
import OutlineBundleHeader from './components/OutlineBundleHeader/OutlineBundleHeader';

import styles from './outlineBundle.module.scss';

export interface OutlineBundleProps {
  onFlashMessageEnter?: () => void;
  onFlashMessageExit?: () => void;
}

export const OutlineBundle: FC<OutlineBundleProps> = memo(
  ({ onFlashMessageEnter, onFlashMessageExit }) => {
    const { isSellable } = useDetail();

    const { applicableCoupon } = useContext(CouponContext);
    const {
      ref,
      state: { isFetching: isLoading, error: recoHasError },
      hasProducts,
      productList,
    } = useProductBundle();
    const [isProductPageRevampEnabled] = useBooleanFlags([
      StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP,
    ]);

    if (
      !isSellable ||
      !hasProducts ||
      recoHasError ||
      (!isLoading && !productList.length)
    )
      return null;

    return (
      <div
        className={classnames(
          styles.container,
          styles.sharedVars,
          !isProductPageRevampEnabled && styles.desktopBlock,
          isProductPageRevampEnabled && '!p-0 desktop:!p-0',
        )}
      >
        <article
          ref={ref}
          data-testid="outline-product-bundle"
          className={classnames({
            [styles.wrapper]: !isProductPageRevampEnabled,
            [styles.revampedWrapper]: isProductPageRevampEnabled,
            [styles.withCoupon]:
              !isProductPageRevampEnabled && applicableCoupon,
            [styles.revampedWithCoupon]:
              isProductPageRevampEnabled && applicableCoupon,
          })}
        >
          <OutlineBundleHeader />
          <OutlineBundleBody />
          {applicableCoupon && (
            <Coupon
              context="bundle"
              percent={
                applicableCoupon.isPercentage
                  ? applicableCoupon.amount
                  : undefined
              }
              {...applicableCoupon}
            />
          )}
          <OutlineBundleA2C />
        </article>
        <FlashMessages
          className={styles.flashMessage}
          onMessageEnter={onFlashMessageEnter}
          onMessageExit={onFlashMessageExit}
        />
      </div>
    );
  },
);

OutlineBundle.displayName = 'OutlineBundle';
